import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Avatar,
    Checkbox,
    Flex,
    List,
    Text,
    Tooltip,
} from "@mantine/core";
import { RowSelectionState } from "@tanstack/react-table";
import dayjs from "dayjs";
import { isArray, isObject } from "lodash";
import { Campaign, Questionnaire, QuestionType } from "src/graphql/generated/graphql";
import { CampaignApplicationRestApi } from "src/types";
import { checkCountryCode, checkGender } from "src/utils/helpers";

import { UseCampaignApplicationsReturn } from "../../hooks/campaignApplications";
import { createColumnHelper, DataGrid } from "../DataGrid";
import { CellRole } from "./cell/role";
import { CellSocials } from "./cell/socials";

const columnHelper = createColumnHelper<CampaignApplicationRestApi>();

export interface CampaignApplicationsDataGridProps extends UseCampaignApplicationsReturn {
    campaign?: Campaign;
    questionnaire?: Questionnaire;
    enableSelection?: boolean;
    showColumnSettings?: boolean
    onClick?(campaignApplication: CampaignApplicationRestApi);
    onSelectedCampaignApplicationsChange?(campaignApplications: CampaignApplicationRestApi[]);
}

export const CampaignApplicationsDataGrid = ({
    allCampaignApplications,
    allCampaignApplicationsUpdateKey,
    campaignApplications,
    isLoadingCampaignApplications,
    questionnaire,
    onClick,
    onSelectedCampaignApplicationsChange,
}: CampaignApplicationsDataGridProps) => {
    const { t } = useTranslation(["common"]);

    const enableSelection = typeof onSelectedCampaignApplicationsChange === "function";
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const columns = useMemo(() => {
        const columns = [
            columnHelper.display({
                id: "profileimage",
                cell: ({ row }) => row.original?.reviewer?.profileImage?.src ?
                    <Avatar src={`${row.original?.reviewer?.profileImage.src}?w=64`} /> :
                    <Avatar>
                        {String(row.original?.reviewer?.profile?.firstName).toUpperCase()[0]}{String(row.original?.reviewer?.profile?.lastName).toUpperCase()[0]}
                    </Avatar>,
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("reviewer.profile", {
                id: "fullname",
                header: "Jméno",
                footer: (props) => props.column.id,
                cell: (props) =>
                    `${props.getValue()?.firstName} ${props.getValue()?.lastName}`,
            }),
            columnHelper.display({
                id: "role",
                header: t("campaignapplication_role"),
                footer: (props) => props.column.id,
                cell: (props) => <CellRole {...props} />,
            }),
            columnHelper.accessor("reviewer.profile.address", {
                id: "revieweraddress",
                header: t("reviewer_address"),
                footer: (props) => props.column.id,
                cell: (props) => {
                    const country = props.getValue()?.country;

                    return [
                        country && checkCountryCode(country) ? t(`country_${country}`) : null,
                        [
                            props.getValue()?.city || null,
                            props.getValue()?.postalCode || null,
                        ].join(" ") || null,
                        props.getValue()?.line1 || null,
                    ].join(", ");
                },
            }),
            columnHelper.accessor("deliveryAddress", {
                id: "deliveryaddress",
                header: t("campaignapplication_deliveryaddress"),
                footer: (props) => props.column.id,
                cell: (props) => {
                    const country = props.getValue()?.country;

                    return [
                        country && checkCountryCode(country) ? t(`country_${country}`) : null,
                        [
                            props.getValue()?.city || null,
                            props.getValue()?.postalCode || null,
                        ].join(" ") || null,
                        props.getValue()?.line1 || null,
                    ].join(", ");
                },
            }),
            columnHelper.display({
                id: "appliedat",
                header: t("campaignapplication_appliedat"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.appliedAt ?
                        dayjs(props.row.original?.appliedAt).format("DD.MM.YYYY HH:mm") : <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "match",
                header: "Shoda",
                footer: (props) => props.column.id,
                cell: () => <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "applicationscount",
                header: t("reviewer_applicationscount"),
                footer: (props) => props.column.id,
                cell: (props) => (
                    <>
                        <Tooltip label={t("reviewer_totalapplicationscount")} withArrow>
                            <Text weight={500} mr="6px" span>
                                {props.row.original?.reviewer?.totalApplicationsCount || 0}
                            </Text>
                        </Tooltip>
                        (<Tooltip label={t("reviewer_selectedapplicationscount")} withArrow>
                            <Text color="green" span>
                                {props.row.original?.reviewer?.selectedApplicationsCount || 0}
                            </Text>
                        </Tooltip>
                        {" / "}
                        <Tooltip label={t("reviewer_alternateapplicationscount")} withArrow>
                            <Text color="yellow" span>
                                {props.row.original?.reviewer?.alternateApplicationsCount || 0}
                            </Text>
                        </Tooltip>
                        {" / "}
                        <Tooltip label={t("reviewer_rejectedapplicationscount")} withArrow>
                            <Text color="red" span>
                                {props.row.original?.reviewer?.rejectedApplicationsCount || 0}
                            </Text>
                        </Tooltip>)
                    </>
                ),
            }),
            columnHelper.display({
                id: "clientapplicationscount",
                header: t("reviewer_clientapplicationscount"),
                footer: (props) => props.column.id,
                cell: (props) => (
                    <>
                        <Tooltip label={t("reviewer_totalapplicationscount")} withArrow>
                            <Text weight={500} mr="6px" span>
                                {props.row.original?.reviewer?.clientCounts?.any || 0}
                            </Text>
                        </Tooltip>
                        (<Tooltip label={t("reviewer_selectedapplicationscount")} withArrow>
                            <Text color="green" span>
                                {props.row.original?.reviewer?.clientCounts?.selected || 0}
                            </Text>
                        </Tooltip>
                        {" / "}
                        <Tooltip label={t("reviewer_alternateapplicationscount")} withArrow>
                            <Text color="yellow" span>
                                {props.row.original?.reviewer?.clientCounts?.alternate || 0}
                            </Text>
                        </Tooltip>
                        {" / "}
                        <Tooltip label={t("reviewer_rejectedapplicationscount")} withArrow>
                            <Text color="red" span>
                                {props.row.original?.reviewer?.clientCounts?.rejected || 0}
                            </Text>
                        </Tooltip>)
                    </>
                ),
            }),
            columnHelper.display({
                id: "discountcode",
                header: t("campaignapplication_discountcode"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.discountCode || <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "isordered",
                header: t("campaignapplication_isordered"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.isOrdered ?
                        t("yes") : t("no"),
            }),
            columnHelper.display({
                id: "orderconfirmedat",
                header: t("campaignapplication_orderconfirmedat"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.orderConfirmedAt ?
                        dayjs(props.row.original?.orderConfirmedAt).format("DD.MM.YYYY HH:mm") : <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "isdelivered",
                header: t("campaignapplication_isdelivered"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.isDelivered ?
                        t("yes") : t("no"),
            }),
            columnHelper.display({
                id: "deliveryconfirmedat",
                header: t("campaignapplication_deliveryconfirmedat"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.deliveryConfirmedAt ?
                        dayjs(props.row.original?.deliveryConfirmedAt).format("DD.MM.YYYY HH:mm") : <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "deadlineat",
                header: t("campaignapplication_deadlineat"),
                footer: (props) => props.column.id,
                cell: (props) =>
                    props.row.original?.deadlineAt ?
                        dayjs(props.row.original?.deadlineAt).format("DD.MM.YYYY HH:mm") : <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "reviewcount",
                header: t("reviews"),
                footer: (props) => props.column.id,
                cell: (props) => props.row.original?.submittedReviewsCount || "0",
            }),
            columnHelper.accessor("reviewer.profile.gender", {
                id: "gender",
                header: t("gender"),
                footer: (props) => props.column.id,
                cell: (props) => {
                    const gender = props.getValue();

                    if (gender && !checkGender(gender)) {
                        return <Text color="gray">-</Text>;
                    }

                    return t(`gender_${gender}`);
                },
            }),
            columnHelper.accessor("reviewer.profile.age", {
                id: "age",
                header: t("age"),
                footer: (props) => props.column.id,
                cell: (props) => typeof props.getValue() === "number" ?
                    t("years_old_count", { count: props.getValue() }) : <Text color="gray">-</Text>,
            }),
            columnHelper.display({
                id: "socials",
                header: t("reviewer_socials"),
                footer: (props) => props.column.id,
                cell: (props) => <CellSocials {...props} />,
            }),
        ];

        if (questionnaire?.questions?.length) {
            for (const question of questionnaire?.questions || []) {
                columns.push(columnHelper.accessor("questionnaireResponse.answers", {
                    id: question?.key,
                    header: question?.text,
                    footer: (props) => props.column.id,
                    cell: (props) => {
                        const answer = props?.getValue?.()?.find(answer => answer?.key === question?.key);

                        try {
                            switch (question?.type) {
                                case QuestionType.Text:
                                case QuestionType.Textarea:
                                case QuestionType.Number:
                                    // Only in case of invalid data
                                    if (isArray(answer?.value)) {
                                        return (
                                            <List>
                                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                                {(answer?.value as any[] || []).map((item, index) => (
                                                    <List.Item
                                                        key={`${index}${item?.key}`}
                                                        color="red"
                                                    >
                                                        {item?.text}
                                                    </List.Item>
                                                ))}
                                            </List>
                                        );
                                    }

                                    // Only in case of invalid data
                                    if (isObject(answer?.value)) {
                                        return (
                                            <Text color="red">
                                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                                {(answer?.value as any)?.text}
                                            </Text>
                                        );
                                    }

                                    return answer?.value;
                                case QuestionType.Checkbox:
                                    return (
                                        <List>
                                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                            {(answer?.value as unknown as any[] || []).map((item, index) => (
                                                <List.Item key={`${index}${item?.key}`}>
                                                    {item?.text}
                                                </List.Item>
                                            ))}
                                        </List>
                                    );
                                case QuestionType.Radio:
                                    return (
                                        <Text>
                                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                            {(answer?.value as any)?.text}
                                        </Text>
                                    );
                                case QuestionType.Date:
                                case QuestionType.DateTime:
                                case QuestionType.Time:
                                    return "FIX ME";
                            }
                        } catch (err) { }

                        return <Text color="gray">-</Text>;
                    },
                }));
            }
        }

        if (enableSelection) {
            columns.unshift(
                columnHelper.display({
                    id: "_rowselection",
                    footer: (props) => props.column.id,
                    header: ({ table }) => (
                        <Checkbox
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                        />
                    ),
                    cell: ({ row }) => (
                        <Flex display="inline-flex" align="center">
                            <Checkbox
                                mr="xs"
                                checked={row.getIsSelected()}
                                disabled={!row.getCanSelect()}
                                indeterminate={row.getIsSomeSelected()}
                                onChange={row.getToggleSelectedHandler()}
                            />
                        </Flex>
                    ),
                }),
            );
        }

        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, enableSelection, JSON.stringify(questionnaire?.questions)]);

    useEffect(() => {
        const selectedCampaignApplications = (allCampaignApplications || []).filter(campaignApplication => {
            return rowSelection?.[campaignApplication?.id];
        });

        onSelectedCampaignApplicationsChange?.(selectedCampaignApplications);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSelection, allCampaignApplicationsUpdateKey, JSON.stringify(rowSelection)]);

    return (
        <DataGrid
            isLoading={isLoadingCampaignApplications}
            columns={columns}
            data={campaignApplications}
            onRowClick={row =>
                onClick?.(row.original)}
            getRowId={campaignApplication => `${campaignApplication?.id}`}
            mobileHeaderIndex={0}
            secMobileHeaderIndex={2}
            enableRowSelection={enableSelection}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
        />
    );
};
